import type { MathflatApi } from '@mathflat/domain/@entities/Login/api'
import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { AxiosError } from 'axios'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { loadInitialData } from '~/@app/init/loadInitialData'
import { authApi } from '~/@common/api'
import { academyApi } from '~/@common/api/academyApi'
import { parentApi } from '~/@common/api/parentApi'
import { resolveSafeRedirectURI } from '~/@common/integration/safeRedirect'
import { webviewService } from '~/@common/services'
import authService from '~/@common/services/auth.service'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { localStorageService } from '~/@common/services/storage.service'
import Button from '~/@common/ui/(Button)/Button/Button'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import { ValidationInput } from '~/@common/ui/ValidationInput'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import maxios from '~/@common/utils/maxios'

import PasswordChangeModal from '../@widgets/PasswordChangeModal'
import S from './LoginPage.style'
import { ExternalLoginButtonRails } from './naver-works/ExternalLoginButtonRails'

type UserMode = 'STUDENT' | 'PARENT'

const LoginPage = () => {
  const [searchParams] = useSearchParams()

  const redirectURI = searchParams.get('redirectURI')

  const navigate = useNavigate()

  const count = useRef(0)
  const [isKeepSignInChecked, setIsKeepSignInChecked] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userMode, setUserMode] = useState<UserMode>('STUDENT')

  // 이전 로그인 시에 핸드폰 번호로 로그인 한 경우에 초기에 폰넘버 모드가 되어야 함.
  const isPhoneNumberReg = /^[0-9]{11}$|^(01[1-9])[0-9]{7}$/
  const [isPhoneNumberMode, setIsPhoneNumberMode] = useState(
    localStorageService.user.beforeLoginId
      ? isPhoneNumberReg.test(localStorageService.user.beforeLoginId)
      : false,
  )

  const [isPhoneNumberPasswordNeeded, setIsPhoneNumberPasswordNeeded] = useState(true)

  const isHiddenPasswordInput = isPhoneNumberMode && !isPhoneNumberPasswordNeeded

  const {
    handleSubmit,
    clearErrors,
    setError,
    reset,
    control,
    formState: { errors },
  } = useForm<MathflatApi.Request.Login>({
    mode: 'onSubmit',
    defaultValues: {
      id: localStorageService.user.beforeLoginId ?? '',
      password: '',
    },
  })

  const isValidPhoneNumber = (phoneNumber: string) => {
    return /^[0-9]{11}$|^(01[1-9])[0-9]{7}$/.test(phoneNumber)
  }

  const checkPasswordChangeRequired = async (
    phoneNumber: string,
    userType: 'STUDENT' | 'PARENT',
  ) => {
    const { data } = await authApi.loginInfo({ phoneNumber, userType })

    return data.isPolicyAgreed
  }

  const validateLoginInput = async (id: string) => {
    const isPhoneNumber = isValidPhoneNumber(id)

    if (isPhoneNumber) {
      try {
        const response = await checkPasswordChangeRequired(id, userMode)
        setIsPhoneNumberPasswordNeeded(response)
      } catch {
        setIsPhoneNumberPasswordNeeded(true)
      }
    }
    setIsPhoneNumberMode(isPhoneNumber)
  }

  const login = async (field: MathflatApi.Request.Login) => {
    setIsLoading(true)

    const setIdentifyEvent = async () => {
      try {
        const academy = await academyApi.getAcademies()
        if (commonRepo.student) {
          window.freshpaint?.identify(commonRepo.student.id, {
            $phone: commonRepo.student.phone,
            $name: commonRepo.student.name,
            unique_id: localStorage.getItem('academyId'),
            ...academy,
            ...commonRepo.student,
          })
        }
      } catch (e) {
        console.error(e)
      }
    }

    try {
      maxios.defaults.headers.common['x-platform'] = userMode

      const user = await authService.loginByJwt(
        {
          id: field.id,
          password: field.password,
          userType: userMode,
          serviceType: 'MATHFLAT',
        },
        isKeepSignInChecked,
        field.id,
      )

      if (user.userType === 'PARENT') {
        try {
          const { studentIds } = await parentApi.getParentRelations(user.userId)
          const firstStudentId = studentIds[0] as StudentDomain.Id

          // 첫 번째 자녀를 기본 학생으로 설정
          localStorageService.update({ user: { studentId: firstStudentId } })

          // 자녀 이름 조회 후 childStudents에 포함 (GNB 드롭다운용)
          const students = await parentApi.getStudents(studentIds)
          commonRepo.parent.setChildStudents(
            students.map((s) => ({ studentId: s.id as StudentDomain.Id, name: s.name ?? '' })),
          )
          commonRepo.parent.setActiveChildStudentId(firstStudentId)

          // loadInitialData에서 academy 정보로 UserPermission을 생성 (userRole: 'PARENT' 전달)
          await loadInitialData(firstStudentId)
        } catch {
          authService.logout()
          return
        }
        navigate(getRedirectUriOrEntrance('PARENT'), { replace: true })
        return
      } else if (user.userType === 'STUDENT') {
        setIdentifyEvent()
      }

      if (field.password === import.meta.env.VITE_STUDENT_INIT_PASSWORD) {
        modalService.openModal(
          { content: <PasswordChangeModal isInitial />, header: '비밀번호 변경' },
          {
            modalName: '최초 진입 비밀번호 변경 모달',
            headerOption: { hasBottomBorder: false },
            onAfterClose: () => {
              navigate(getRedirectUriOrEntrance('STUDENT'), { replace: true })
            },
          },
        )
      } else {
        navigate(getRedirectUriOrEntrance('STUDENT'), { replace: true })
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.data.code === 'USER_NOT_FOUND') {
          setError('id', {
            message: '아이디를 다시 확인해주세요.',
          })
        }

        if (error.response?.data.code === 'USER_WRONG_PASSWORD') {
          setError('password', {
            message: '비밀번호를 다시 확인해주세요.',
          })
        }

        if (error.response?.data.code === 'USER_EXPIRED') {
          setError('id', {
            message: '현재 유저는 사용중지되었습니다.',
          })
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmit = (field: MathflatApi.Request.Login) => {
    // 비밀번호 입력칸이 렌더링되지 않는 경우(전화번호 로그인) password가 field에서 나타나지 않기 때문에 주입
    if (!field.password) {
      field.password = ''
    }

    login(field)
  }

  const handleTabClick = (userMode: UserMode) => {
    setUserMode(userMode)
    setIsPhoneNumberMode(false)
    reset()
  }

  // redirectURI가 있으면 로그인 후 원래 페이지로 이동, 없으면 기본 페이지.
  // 오픈 리다이렉트 방지: '/'로 시작하고 '//'가 아닌 경우만 허용
  const getRedirectUriOrEntrance = (userType: Parameters<typeof getEntrancePage>[0]) => {
    return resolveSafeRedirectURI(redirectURI) ?? getEntrancePage(userType)
  }

  // 이미 로그인된 상태로 /login 진입 시 기본 페이지로 리다이렉트
  useEffect(() => {
    if (authService.isLoggedIn) {
      const { studentId } = localStorageService.user
      if (studentId) {
        loadInitialData(studentId).finally(() => {
          navigate(getRedirectUriOrEntrance(authService.userType), { replace: true })
        })
      } else {
        navigate(getRedirectUriOrEntrance(authService.userType), { replace: true })
      }
    }
  }, [])

  useEffect(() => {
    const beforeLoginId = localStorageService.user.beforeLoginId
    if (beforeLoginId) {
      validateLoginInput(beforeLoginId)
    }
  }, [])

  return (
    <>
      <PortalRootLayoutHeader />
      <S.Container>
        {commonRepo.isSchoolflat ? (
          <img
            className="title"
            src={s3URL.student('assets/schoolflat/logo.png')}
            alt="스쿨플랫 로그인"
            width={180}
          />
        ) : (
          <img
            className="title"
            src={s3URL.student('images/student-login-text.png')}
            alt="로그인"
            width={54}
          />
        )}
        <div className="contents">
          <div className="tab">
            <div
              className={clsx('tab-item', userMode === 'STUDENT' && 'selected')}
              onClick={() => handleTabClick('STUDENT')}
            >
              학생으로
            </div>
            <div
              className={clsx('tab-item', userMode === 'PARENT' && 'selected')}
              onClick={() => handleTabClick('PARENT')}
            >
              학부모로
            </div>
          </div>
          <div className="form">
            <div
              className={clsx(
                'input-container',
                errors.id && 'id-error',
                errors.password && 'password-error',
              )}
            >
              <Controller
                name="id"
                control={control}
                rules={{ required: '아이디 또는 휴대폰번호를 입력해주세요.' }}
                render={({ field: { onChange, value } }) => (
                  <ValidationInput
                    name="id"
                    className="id-input"
                    type="text"
                    value={value}
                    placeholder="아이디 또는 휴대폰번호를 입력해주세요."
                    errorMessage={errors.id?.message}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    onChange={(e) => {
                      onChange(e)
                      clearErrors()
                      validateLoginInput(e.target.value)
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                rules={{ required: !isHiddenPasswordInput ? '비밀번호를 입력해주세요.' : false }}
                render={({ field: { onChange, value } }) => (
                  <ValidationInput
                    name="password"
                    className={clsx('password-input', isHiddenPasswordInput && 'hidden')}
                    type="password"
                    value={value}
                    placeholder="비밀번호를 입력해주세요."
                    errorMessage={errors.password?.message}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSubmit(onSubmit)()
                      }
                    }}
                    onChange={(e) => {
                      onChange(e)
                      clearErrors()
                    }}
                  />
                )}
              />
            </div>

            <Button
              className={clsx('submit-button', commonRepo.isSchoolflat && 'schoolflat')}
              size="small"
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoading}
              disabled={isLoading}
            >
              시작하기
            </Button>
            {commonRepo.isSchoolflat && <ExternalLoginButtonRails />}
            <label
              className={clsx('login-checkbox', commonRepo.isSchoolflat && 'schoolflat')}
              htmlFor="keepSignIn"
            >
              <input
                id="keepSignIn"
                type="checkbox"
                checked={isKeepSignInChecked}
                onChange={() => setIsKeepSignInChecked(!isKeepSignInChecked)}
              />
              <span className="checkbox_label" />
              <span className="checkbox_label_text">로그인 상태 유지</span>
            </label>
          </div>
        </div>
      </S.Container>
      <S.Guide
        onClick={() => {
          count.current += 1
          if (count.current >= 5) {
            webviewService.openDevTool()
            count.current = 0
          }
        }}
      >
        아이디와 비밀번호를 모를 경우
        <br />
        선생님에게 문의해주세요.
      </S.Guide>
    </>
  )
}

export default observer(LoginPage)
